/**
 * cliente-portal-store — React Query hooks pros endpoints
 * /api/v1/cliente/* autenticados (B1.6 consome backend B1.2).
 *
 * Endpoints cobertos:
 *   - GET    /dashboard               → KPIs + permissões resolvidas server-side
 *   - GET    /chamados                → lista todos (Gestor com ver_todos_chamados)
 *   - GET    /meus-chamados           → lista PRÓPRIOS (sempre disponível)
 *   - GET    /chamado/:id             → detalhe (timeline filtrada visivel_cliente=1)
 *   - POST   /chamados                → multipart (titulo + descricao + foto opcional)
 *   - POST   /chamado/:id/comentario  → adiciona comentário cliente
 *   - GET    /chamado/:id/foto-url    → presigned URL pra ver foto R2
 *
 * Header X-Client-Context é injetado automaticamente pelo cliente-api
 * via setActiveClientContext (configurado pelo ClienteContext).
 *
 * NÃO REVERTER:
 *   - Query keys incluem clientContext (cache isolado por cliente)
 *   - Invalidation a cada mutation (chamados/comentários)
 *   - Tipos espelham backend (não derive — duplica pra clareza)
 *   - createChamadoComFoto usa FormData (multipart) — cliente-api detecta
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clienteApi, getActiveClientContext } from './cliente-api';

// ─── Tipos espelhando backend B1.2 ────────────────────────────────────

export interface DashboardKpis {
  /** Chamados abertos do cliente (fase aberta | em-atendimento). */
  chamadosAbertos: number;
  /** Chamados que ELE MESMO abriu (sempre disponível, mesmo Portaria). */
  meusChamados: number;
  /** Contratos status='ativo' do cliente. */
  contratosAtivos: number;
}

export interface DashboardResponse {
  cliente: { id: string; nome: string };
  role: 'gestor' | 'portaria';
  permissoes: Record<string, boolean>;
  kpis: DashboardKpis;
  // F3.2: flags de feature pro frontend (sino de notificações etc).
  features?: { projetosNotificacoes?: boolean };
}

/** Shape REAL do backend listChamadosByCliente/byAutor (sem faseLabel/prioridade
 * — labels são geradas no frontend via FASE_LABELS). */
export interface ChamadoListItem {
  id: string;
  numeroHumano: number | null;
  titulo: string;
  fase: string;
  criadoEmIso: string;
  atualizadoEmIso: string;
  /** UserId do criador (uso só interno — frontend resolve nome se for o user atual). */
  criadoPor?: string;
}

/** Shape REAL do backend getChamadoForCliente — comentários NÃO incluem
 * autorTipo (calculado no frontend comparando autorId com user.id). */
export interface ChamadoComentario {
  id: string;
  corpo: string;
  autorId: string;
  autorNome: string;
  criadoEmIso: string;
  visivelCliente: boolean;
}

export interface ChamadoDetail {
  id: string;
  numeroHumano: number | null;
  titulo: string;
  descricao: string;
  fase: string;
  criadoEmIso: string;
  criadoPor: string;
  fotoR2Key: string | null;
  origem: string;
  comentarios: ChamadoComentario[];
}

/**
 * Map fase slug → label legível pra UI (backend não envia label).
 * Espelha colunas seedadas em migration 145 pra workflow 'corretiva':
 *   aberta    → "Abertas"     warning
 *   agendada  → "Agendadas"   info
 *   cancelada → "Canceladas"  danger
 *   concluida → "Concluídas"  success
 *
 * Slugs SINGULAR mantidos pra preservar compat backend (~73 tests +
 * chamados em prod gravados antes da migration 145). UI mostra plural.
 * Legacy ('em-atendimento'/'finalizado') tolerados via fallback pro slug
 * cru — não devem aparecer pós-migration 145 que reescreveu deals.
 */
export const FASE_LABELS: Record<string, string> = {
  aberta: 'Abertas',
  agendada: 'Agendadas',
  cancelada: 'Canceladas',
  concluida: 'Concluídas',
  // Legacy fallbacks (defesa)
  'em-atendimento': 'Em Atendimento',
  finalizado: 'Finalizado',
};

/** Variant Badge por fase — pra renderizar consistente cross-component. */
export const FASE_BADGE_VARIANT: Record<string, 'warning' | 'info' | 'danger' | 'success' | 'neutral'> = {
  aberta: 'warning',
  agendada: 'info',
  cancelada: 'danger',
  concluida: 'success',
  // Legacy fallbacks
  'em-atendimento': 'info',
  finalizado: 'success',
};

export function faseLabel(fase: string): string {
  return FASE_LABELS[fase] ?? fase;
}

export function faseBadgeVariant(fase: string): 'warning' | 'info' | 'danger' | 'success' | 'neutral' {
  return FASE_BADGE_VARIANT[fase] ?? 'neutral';
}

export interface CreateChamadoInput {
  titulo: string;
  descricao: string;
  foto?: File | null;
}

/** Shape REAL backend POST /chamados:
 * { chamado: { id, numeroHumano, fase, fotoR2Key, notificadosEquipe } }
 * — wrapper `chamado` foi a divergência que causou bug "Chamado não
 * encontrado" reportado pelo Matheus 2026-05-28 noite. */
export interface CreateChamadoResponse {
  chamado: {
    id: string;
    numeroHumano: number | null;
    fase: string;
    fotoR2Key: string | null;
    notificadosEquipe: number;
  };
}

export interface AddComentarioInput {
  corpo: string;
}

// ─── Hooks dashboard ───────────────────────────────────────────────────

export function useClienteDashboard() {
  const clientContext = getActiveClientContext();
  return useQuery({
    queryKey: ['cliente', 'dashboard', clientContext] as const,
    queryFn: () => clienteApi.get<DashboardResponse>('/dashboard'),
    enabled: !!clientContext,
    staleTime: 30_000,
  });
}

// ─── Hooks chamados (list) ─────────────────────────────────────────────

/**
 * Lista TODOS chamados do cliente (Gestor com ver_todos_chamados).
 * Portaria recebe 403 — caller deve guard via permissions do dashboard.
 *
 * Backend retorna `{ chamados: [...] }` — desempacotamos no queryFn pra
 * UI continuar consumindo `data.items` (shape estável + consistente com
 * conveções React Query de paginação futura).
 */
export function useChamadosTodos(enabled = true) {
  const clientContext = getActiveClientContext();
  return useQuery({
    queryKey: ['cliente', 'chamados', clientContext] as const,
    queryFn: async () => {
      const r = await clienteApi.get<{ chamados: ChamadoListItem[] }>('/chamados');
      return { items: r.chamados ?? [] };
    },
    enabled: !!clientContext && enabled,
    staleTime: 30_000,
  });
}

/**
 * Lista PRÓPRIOS chamados (sempre disponível pra qualquer perfil).
 *
 * Backend retorna `{ chamados: [...] }` — desempacotamos no queryFn.
 */
export function useMeusChamados() {
  const clientContext = getActiveClientContext();
  return useQuery({
    queryKey: ['cliente', 'meus-chamados', clientContext] as const,
    queryFn: async () => {
      const r = await clienteApi.get<{ chamados: ChamadoListItem[] }>('/meus-chamados');
      return { items: r.chamados ?? [] };
    },
    enabled: !!clientContext,
    staleTime: 30_000,
  });
}

// ─── Hook detail ───────────────────────────────────────────────────────

/**
 * Backend retorna `{ chamado: {...} }` — desempacotamos no queryFn pro
 * componente continuar usando `data.titulo` etc sem indireção `data.chamado.x`.
 * Mesmo padrão do POST /chamados (BUG-B1.6-RESPONSE-SHAPE original).
 */
export function useChamadoDetail(chamadoId: string | null | undefined) {
  const clientContext = getActiveClientContext();
  return useQuery({
    queryKey: ['cliente', 'chamado', clientContext, chamadoId ?? ''] as const,
    queryFn: async () => {
      const r = await clienteApi.get<{ chamado: ChamadoDetail }>(
        `/chamado/${encodeURIComponent(chamadoId!)}`,
      );
      return r.chamado;
    },
    enabled: !!clientContext && !!chamadoId,
    staleTime: 30_000,
  });
}

/**
 * Presigned URL pra ver foto R2. Curto TTL (10min) — não cachear muito.
 */
export function useChamadoFotoUrl(chamadoId: string | null | undefined, fotoR2Key: string | null) {
  const clientContext = getActiveClientContext();
  return useQuery({
    queryKey: ['cliente', 'chamado', 'foto-url', clientContext, chamadoId ?? ''] as const,
    queryFn: () =>
      clienteApi.get<{ url: string }>(`/chamado/${encodeURIComponent(chamadoId!)}/foto-url`),
    enabled: !!clientContext && !!chamadoId && !!fotoR2Key,
    staleTime: 5 * 60_000, // 5min (URL presigned dura ~10min)
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────

/**
 * Cria chamado com upload de foto multipart. Backend valida tamanho ≤10MB
 * + tipo (image/*). Retorna { id, numeroHumano } pra navegar pro detail.
 */
export function useCreateChamado() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateChamadoInput) => {
      const fd = new FormData();
      fd.append('titulo', input.titulo);
      fd.append('descricao', input.descricao);
      if (input.foto) fd.append('foto', input.foto);
      return clienteApi.post<CreateChamadoResponse>('/chamados', fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cliente', 'chamados'] });
      qc.invalidateQueries({ queryKey: ['cliente', 'meus-chamados'] });
      qc.invalidateQueries({ queryKey: ['cliente', 'dashboard'] });
    },
  });
}

/**
 * Adiciona comentário cliente ao chamado. SEMPRE visivel_cliente=1.
 *
 * Backend retorna `{ comentarioId: '...' }` — desempacotamos pra `{ id }`
 * pra consumidor não ter que conhecer naming idiossincrático do server.
 */
export function useAddComentarioChamado(chamadoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddComentarioInput) => {
      const r = await clienteApi.post<{ comentarioId: string }>(
        `/chamado/${encodeURIComponent(chamadoId)}/comentario`,
        input,
      );
      return { id: r.comentarioId };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cliente', 'chamado'] });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════
// B1.7 — Orçamentos / Contratos / Manutenções / Financeiro (Gestor only)
// ═══════════════════════════════════════════════════════════════════════

export interface OrcamentoListItem {
  id: string;
  numeroHumano: number;
  titulo: string;
  fase: string;
  valor: number; // em centavos (backend deals padrão)
  criadoEmIso: string;
  atualizadoEmIso: string;
}

export interface OrcamentoDetail {
  id: string;
  numeroHumano: number;
  titulo: string;
  descricao: string;
  fase: string;
  valor: number;
  prioridade: string;
  criadoEmIso: string;
  atualizadoEmIso: string;
}

export function useOrcamentos() {
  const clientContext = getActiveClientContext();
  return useQuery({
    queryKey: ['cliente', 'orcamentos', clientContext] as const,
    queryFn: async () => {
      const r = await clienteApi.get<{ orcamentos: OrcamentoListItem[] }>('/orcamentos');
      return { items: r.orcamentos ?? [] };
    },
    enabled: !!clientContext,
    staleTime: 30_000,
  });
}

export function useOrcamentoDetail(id: string | null | undefined) {
  const clientContext = getActiveClientContext();
  return useQuery({
    queryKey: ['cliente', 'orcamento', clientContext, id ?? ''] as const,
    queryFn: async () => {
      const r = await clienteApi.get<{ orcamento: OrcamentoDetail }>(
        `/orcamento/${encodeURIComponent(id!)}`,
      );
      return r.orcamento;
    },
    enabled: !!clientContext && !!id,
    staleTime: 30_000,
  });
}

export function useAprovarOrcamento(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { observacao?: string }) =>
      clienteApi.post<{ ok: true; decisao: 'aprovado'; comentarioId: string }>(
        `/orcamento/${encodeURIComponent(id)}/aprovar`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cliente', 'orcamento'] });
      qc.invalidateQueries({ queryKey: ['cliente', 'orcamentos'] });
    },
  });
}

export function useRejeitarOrcamento(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { motivo: string }) =>
      clienteApi.post<{ ok: true; decisao: 'rejeitado'; comentarioId: string }>(
        `/orcamento/${encodeURIComponent(id)}/rejeitar`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cliente', 'orcamento'] });
      qc.invalidateQueries({ queryKey: ['cliente', 'orcamentos'] });
    },
  });
}

export interface ContratoListItem {
  id: string;
  numero: string | null;
  tipo: string;
  inicio_iso: string;
  fim_previsto_iso: string | null;
  fim_real_iso: string | null;
  status: string;
}

export function useContratos() {
  const clientContext = getActiveClientContext();
  return useQuery({
    queryKey: ['cliente', 'contratos', clientContext] as const,
    queryFn: async () => {
      const r = await clienteApi.get<{ contratos: ContratoListItem[] }>('/contratos');
      return { items: r.contratos ?? [] };
    },
    enabled: !!clientContext,
    staleTime: 60_000,
  });
}

export interface ManutencaoListItem {
  id: string;
  numeroHumano: number;
  titulo: string;
  fase: string;
  dataAgendada: string | null;
  criadoEmIso: string;
}

export function useManutencoes() {
  const clientContext = getActiveClientContext();
  return useQuery({
    queryKey: ['cliente', 'manutencoes', clientContext] as const,
    queryFn: async () => {
      const r = await clienteApi.get<{ manutencoes: ManutencaoListItem[] }>('/manutencoes');
      return { items: r.manutencoes ?? [] };
    },
    enabled: !!clientContext,
    staleTime: 60_000,
  });
}

// ─── Projetos (F3.1) — Gestor acompanha etapas/datas/%, SEM valores ──────

export interface ProjetoEtapaCliente {
  id: string;
  nome: string;
  status: string;
  parentEtapaId: string | null;
  ordem: number;
  dataInicio: string | null;
  dataFim: string | null;
  percentual: number;
}

export interface ProjetoCliente {
  id: string;
  nome: string;
  status: string | null;
  tipoNome: string | null;
  dataInicio: string | null;
  dataFim: string | null;
  dataInicioReal: string | null;
  dataFimReal: string | null;
  percentual: number;
  etapas: ProjetoEtapaCliente[];
}

export function useProjetosCliente() {
  const clientContext = getActiveClientContext();
  return useQuery({
    queryKey: ['cliente', 'projetos', clientContext] as const,
    queryFn: async () => {
      const r = await clienteApi.get<{ projetos: ProjetoCliente[] }>('/projetos');
      return { items: r.projetos ?? [] };
    },
    enabled: !!clientContext,
    staleTime: 60_000,
  });
}

// ─── Notificações (F3.2) — user-level (sino do Portal) ────────────────────

export interface ClienteNotificacao {
  id: string;
  type: string;
  title: string;
  body: string;
  linkUrl: string | null;
  lida: boolean;
  criadoEmIso: string;
}

export interface ClienteNotificacoesResponse {
  items: ClienteNotificacao[];
  naoLidas: number;
}

export function useClienteNotificacoes(enabled = true) {
  return useQuery({
    queryKey: ['cliente', 'notificacoes'] as const,
    queryFn: () => clienteApi.get<ClienteNotificacoesResponse>('/notificacoes'),
    // Sino sem WebSocket no portal → polling 60s (fallback robusto).
    refetchInterval: enabled ? 60_000 : false,
    enabled,
    staleTime: 30_000,
  });
}

export function useMarcarNotificacaoLida() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      clienteApi.put(`/notificacoes/${encodeURIComponent(id)}/lida`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cliente', 'notificacoes'] }),
  });
}

export function useMarcarTodasNotificacoesLidas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => clienteApi.put('/notificacoes/marcar-todas-lidas', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cliente', 'notificacoes'] }),
  });
}

export interface FinanceiroResponse {
  kpis: {
    contratosAtivos: number;
    valorMensalRecorrenteTotal: number;
  };
  historico: Array<unknown>;
  stub?: boolean;
}

export function useFinanceiro() {
  const clientContext = getActiveClientContext();
  return useQuery({
    queryKey: ['cliente', 'financeiro', clientContext] as const,
    queryFn: () => clienteApi.get<FinanceiroResponse>('/financeiro'),
    enabled: !!clientContext,
    staleTime: 60_000,
  });
}

// ═══════════════════════════════════════════════════════════════════════
// B1.8 — Equipe (Gestor add/remove Portaria) + Perfil + Trocar senha
// ═══════════════════════════════════════════════════════════════════════

export interface EquipeMember {
  vinculoId: string;
  userId: string;
  /** Vazio para a portaria sem e-mail (26/10/2026): aí vale `usuario`. */
  email: string;
  usuario?: string | null;
  semEmail?: boolean;
  nome: string;
  role: 'gestor' | 'portaria';
  isPrincipal: boolean;
  status: string;
}

export function useEquipe() {
  const clientContext = getActiveClientContext();
  return useQuery({
    queryKey: ['cliente', 'equipe', clientContext] as const,
    queryFn: async () => {
      const r = await clienteApi.get<{ equipe: EquipeMember[] }>('/equipe');
      return { items: r.equipe ?? [] };
    },
    enabled: !!clientContext,
    staleTime: 30_000,
  });
}

export interface AdicionarPortariaInput {
  email: string;
  nome: string;
}

export interface AdicionarPortariaResult {
  acao: 'vinculado' | 'criado_com_convite';
  userId: string;
  carId: string;
  conviteId?: string;
  conviteToken?: string;
  conviteExpiraEm?: string;
  emailEnviado?: boolean;
  deliveryStatus?: 'sent' | 'failed' | 'stubbed';
  mensagem?: string;
}

export function useAdicionarPortaria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AdicionarPortariaInput) =>
      clienteApi.post<AdicionarPortariaResult>('/equipe/portaria', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cliente', 'equipe'] });
    },
  });
}

/** 26/10/2026 — o síndico cria a portaria SEM e-mail: usuário e senha, sem convite. */
export function useAdicionarPortariaSemEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { nome: string; usuario: string; senha: string }) =>
      clienteApi.post<{ acao: 'criado_sem_email'; userId: string; usuario: string }>('/equipe/portaria-sem-email', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cliente', 'equipe'] });
    },
  });
}

/** O "esqueci a senha" da portaria sem e-mail: o síndico define uma nova. */
export function useDefinirSenhaPortaria() {
  return useMutation({
    mutationFn: ({ userId, senha }: { userId: string; senha: string }) =>
      clienteApi.put<{ ok: true }>(`/equipe/portaria/${encodeURIComponent(userId)}/senha`, { senha }),
  });
}

export function useRemoverPortaria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      clienteApi.delete<{ ok: boolean }>(`/equipe/portaria/${encodeURIComponent(userId)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cliente', 'equipe'] });
    },
  });
}

// ─── Perfil ─────────────────────────────────────────────────────────────

/**
 * Gate FASE-C: o tipo declarava `{id, nome}`, mas GET /perfil devolve
 * `{clientId, clienteNome}` (mesma forma de /meus-clientes). O nome do
 * condomínio saía VAZIO na tela e a key da lista virava undefined (aviso do
 * React). Tipo alinhado ao contrato real do backend.
 */
export interface PerfilResponse {
  user: { id: string; email: string; nome: string };
  clientes: Array<{
    clientId: string;
    clienteNome: string;
    role: string;
    isPrincipal?: boolean;
  }>;
}

export function usePerfil() {
  return useQuery({
    queryKey: ['cliente', 'perfil'] as const,
    queryFn: () => clienteApi.get<PerfilResponse>('/perfil'),
    staleTime: 60_000,
  });
}

export interface TrocarSenhaInput {
  senha_atual: string;
  nova_senha: string;
}

export function useTrocarSenha() {
  return useMutation({
    mutationFn: (input: TrocarSenhaInput) =>
      clienteApi.put<{ ok: true }>('/perfil/senha', input),
  });
}
